import React from 'react';
import { AVEList } from '../../ProData';
import { Helmet } from "react-helmet-async";
import { BookOpen, Calendar, Sparkles, Feather } from 'lucide-react';
import { motion } from 'framer-motion';

export default function ACE() {
  return (
    <>
      <Helmet>
        <title>AVE Reflection - Vidyajyoti College of Theology - Delhi</title>
        <meta name="description" content="AVE Reflection - Daily Meditations on Lectionary Readings by Vidyajyoti Students" />
        <meta name="keywords" content="AVE Reflection, Vidyajyoti publications, Daily Meditations, Lectionary, Catholic Theology" />
        <link rel="canonical" href="/publications/ace" />
      </Helmet>

      <div className="py-5" style={{ background: '#f8fafc', minHeight: '80vh' }}>
        <div className="container">
          {/* Header Banner */}
          <div className="text-center mx-auto pb-4" style={{ maxWidth: '800px' }}>
            <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(121, 3, 29, 0.08)', color: '#79031d', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(121, 3, 29, 0.2)' }}>
              <Sparkles size={14} /> Student Publication
            </div>
            <h1 className="display-6 fw-bold mb-3" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
              AVE Reflection
            </h1>
            <p className="lead text-muted" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
              A monthly publication brought out by the students of Vidyajyoti in English, offering daily contemplative meditations and pastoral insights on the liturgical Lectionary readings.
            </p>
          </div>

          {/* Highlights Box */}
          <div className="row g-4 mb-5 justify-content-center">
            <div className="col-md-4">
              <div className="p-4 rounded-4 bg-white h-100 shadow-sm border text-center">
                <div className="d-inline-flex p-3 rounded-circle mb-3" style={{ background: 'rgba(26, 43, 86, 0.08)', color: '#1a2b56' }}>
                  <Calendar size={24} />
                </div>
                <h5 className="fw-bold mb-1" style={{ color: '#1a2b56' }}>Monthly Edition</h5>
                <p className="text-muted small mb-0">Published every month throughout the academic year for student and pastoral formation.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-4 rounded-4 bg-white h-100 shadow-sm border text-center">
                <div className="d-inline-flex p-3 rounded-circle mb-3" style={{ background: 'rgba(198, 146, 20, 0.12)', color: '#c69214' }}>
                  <BookOpen size={24} />
                </div>
                <h5 className="fw-bold mb-1" style={{ color: '#1a2b56' }}>Lectionary Meditations</h5>
                <p className="text-muted small mb-0">Rooted in Scripture and contextualized for contemporary Indian society and daily life.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-4 rounded-4 bg-white h-100 shadow-sm border text-center">
                <div className="d-inline-flex p-3 rounded-circle mb-3" style={{ background: 'rgba(121, 3, 29, 0.08)', color: '#79031d' }}>
                  <Feather size={24} />
                </div>
                <h5 className="fw-bold mb-1" style={{ color: '#1a2b56' }}>Student Editorial</h5>
                <p className="text-muted small mb-0">Edited, designed, and curated entirely by the student theological fraternity of Vidyajyoti.</p>
              </div>
            </div>
          </div>

          {/* Publication Covers Grid */}
          <div className="text-center mb-4">
            <h3 className="fw-bold" style={{ color: '#1a2b56', fontSize: '1.4rem' }}>Featured Issues</h3>
          </div>

          <div className="row g-4 justify-content-center">
            {AVEList && AVEList.map((item, index) => (
              <motion.div
                key={index}
                className="col-sm-6 col-md-4 col-lg-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div 
                  className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden position-relative bg-white"
                  style={{ transition: 'transform 0.3s ease, box-shadow 0.3s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
                  }}
                >
                  <div className="position-relative overflow-hidden" style={{ height: '320px', background: '#e2e8f0' }}>
                    <img
                      src={item.poster}
                      className="w-100 h-100"
                      alt={item.book || 'AVE Publication Issue'}
                      style={{ objectFit: 'cover' }}
                    />
                    <div 
                      className="position-absolute bottom-0 start-0 end-0 p-3 text-white"
                      style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)' }}
                    >
                      <span className="badge px-2.5 py-1 rounded-pill mb-1" style={{ background: '#c69214', color: '#1a2b56', fontWeight: 600 }}>
                        {item.year || 'Monthly Edition'}
                      </span>
                      <h6 className="fw-bold text-white mb-0 text-truncate" title={item.book}>
                        {item.book || 'AVE Meditation'}
                      </h6>
                    </div>
                  </div>
                  <div className="card-body p-3 d-flex flex-column justify-content-between">
                    <div>
                      <p className="text-muted small mb-1">
                        <strong>Author / Contributor:</strong> {item.author || 'Vidyajyoti Theological Fraternity'}
                      </p>
                      {item.publisher && (
                        <p className="text-muted small mb-0">
                          <small>{item.publisher}</small>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Subscription / Inquiry Note */}
          <div className="mt-5 p-4 rounded-4 text-center border" style={{ background: 'linear-gradient(135deg, rgba(26,43,86,0.03) 0%, rgba(198,146,20,0.05) 100%)' }}>
            <h5 className="fw-bold mb-2" style={{ color: '#1a2b56' }}>Interested in receiving AVE Reflection?</h5>
            <p className="text-muted small mb-3" style={{ maxWidth: '600px', margin: '0 auto' }}>
              For subscription inquiries, bulk copies, or digital archive access, please reach out to the Vidyajyoti Publications Bureau.
            </p>
            <a 
              href="mailto:vjtoday19@gmail.com" 
              className="btn btn-sm px-4 py-2 rounded-pill text-white fw-semibold"
              style={{ background: '#79031d', border: 'none' }}
            >
              Contact Publications Desk
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
